import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { admin, categories as categoriesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Save, RefreshCw, AlertCircle, Calendar, Package, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { debounce } from 'lodash';

const AdminInventory = () => {
  usePageTitle('Inventory');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [changes, setChanges] = useState({}); // Track unsaved changes
  const [etaEditing, setEtaEditing] = useState(null); // Track which ETA is being edited
  const [importing, setImporting] = useState(false);
  const [addStockInputs, setAddStockInputs] = useState({}); // Track "add stock" input values
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [inventoryRes, categoriesRes] = await Promise.all([
        admin.getInventory(),
        categoriesApi.getAll()
      ]);
      setProducts(inventoryRes.data.products);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // Export inventory to CSV
  const handleExport = async () => {
    try {
      const response = await admin.exportInventoryCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Inventory exported successfully');
    } catch (error) {
      toast.error('Failed to export inventory');
    }
  };

  // Import inventory from CSV
  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    try {
      const response = await admin.importInventoryCSV(file);
      const result = response.data;
      
      if (result.total_errors > 0) {
        toast.warning(`Import complete with ${result.total_errors} errors. Updated ${result.updated} entries.`);
      } else {
        toast.success(`Successfully updated ${result.updated} inventory entries`);
      }
      
      // Reload data
      await loadData();
      setChanges({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import inventory');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Debounced save for individual cell changes - ADD mode
  const debouncedAddStock = useCallback(
    debounce(async (productId, size, quantityToAdd, eta, currentQty) => {
      if (quantityToAdd === 0) return; // Don't save if adding 0
      
      try {
        await admin.updateInventory({
          product_id: productId,
          size: size,
          quantity: quantityToAdd,
          eta: eta || null,
          add_mode: true  // ADD to existing stock
        });
        
        // Update local state with new total
        const newTotal = currentQty + quantityToAdd;
        setProducts(prev => prev.map(p => {
          if (p.id === productId) {
            return {
              ...p,
              inventory: {
                ...p.inventory,
                [size]: { ...p.inventory[size], quantity: newTotal }
              }
            };
          }
          return p;
        }));
        
        // Clear the add stock input
        setAddStockInputs(prev => {
          const newInputs = { ...prev };
          delete newInputs[`${productId}-${size}`];
          return newInputs;
        });
        
        toast.success(`Added ${quantityToAdd} to ${size} stock (now ${newTotal})`);
      } catch (error) {
        toast.error(`Failed to update ${size} stock`);
      }
    }, 800),
    []
  );

  // Handle add stock input change
  const handleAddStockChange = (productId, size, value, currentEta, currentQty) => {
    const key = `${productId}-${size}`;
    const numValue = parseInt(value) || 0;
    
    // Update the add stock input value
    setAddStockInputs(prev => ({
      ...prev,
      [key]: numValue
    }));
  };

  // Handle add stock submit (on blur or enter)
  const handleAddStockSubmit = (productId, size, currentEta, currentQty) => {
    const key = `${productId}-${size}`;
    const quantityToAdd = addStockInputs[key] || 0;
    
    if (quantityToAdd !== 0) {
      debouncedAddStock(productId, size, quantityToAdd, currentEta, currentQty);
    }
  };

  // Legacy function for direct quantity setting (used in some places)
  const handleQuantityChange = (productId, size, value, currentEta) => {
    const key = `${productId}-${size}`;
    const numValue = parseInt(value) || 0;
    
    // Update local state immediately
    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        return {
          ...p,
          inventory: {
            ...p.inventory,
            [size]: { ...p.inventory[size], quantity: numValue }
          }
        };
      }
      return p;
    }));
    
    // Mark as changed
    setChanges(prev => ({
      ...prev,
      [key]: { productId, size, quantity: numValue, eta: currentEta }
    }));
  };

  const handleEtaChange = async (productId, size, eta) => {
    const product = products.find(p => p.id === productId);
    const currentQty = product?.inventory[size]?.quantity || 0;
    
    // Update local state
    setProducts(prev => prev.map(p => {
      if (p.id === productId) {
        return {
          ...p,
          inventory: {
            ...p.inventory,
            [size]: { ...p.inventory[size], eta: eta || null }
          }
        };
      }
      return p;
    }));
    
    // Save immediately
    try {
      await admin.updateInventory({
        product_id: productId,
        size: size,
        quantity: currentQty,
        eta: eta || null
      });
      toast.success('ETA updated');
    } catch (error) {
      toast.error('Failed to update ETA');
    }
    
    setEtaEditing(null);
  };

  const handleSaveAll = async () => {
    if (Object.keys(changes).length === 0) {
      toast.info('No changes to save');
      return;
    }
    
    setSaving(true);
    try {
      const updates = Object.values(changes).map(change => ({
        product_id: change.productId,
        size: change.size,
        quantity: change.quantity,
        eta: change.eta
      }));
      
      await admin.bulkUpdateInventory(updates);
      setChanges({});
      toast.success(`Saved ${updates.length} inventory changes`);
    } catch (error) {
      toast.error('Failed to save some changes');
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  // Group products by category
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const category = product.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {});

  // Sort categories alphabetically
  const sortedCategories = Object.keys(groupedProducts).sort();

  const allSizes = ['S', 'M', 'L', 'XL', 'XXL', 'OS'];
  const hasUnsavedChanges = Object.keys(changes).length > 0;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 w-1/4 rounded" />
          <div className="h-[500px] bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-600">Manage stock levels across all products</p>
        </div>
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
              {Object.keys(changes).length} unsaved changes
            </Badge>
          )}
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={handleSaveAll} 
            disabled={!hasUnsavedChanges || saving}
            className="bg-[#002FA7]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>

      {/* CSV Import/Export Section */}
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-[#002FA7]" />
            <div>
              <p className="font-medium text-[#0F172A]">Bulk Update via CSV</p>
              <p className="text-sm text-[#64748B]">Export inventory, edit in Excel, and import back</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImport}
                accept=".csv"
                className="hidden"
                id="csv-upload"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importing...' : 'Import CSV'}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-3 p-3 bg-white rounded border border-[#E2E8F0]">
          <p className="text-xs text-[#64748B]">
            <strong>CSV Format:</strong> Product ID, SKU, Product Name, Category, Size, Current Stock, ETA (YYYY-MM-DD), Price
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Category:</span>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-gray-500">
          {filteredProducts.length} products
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6 flex items-start gap-3">
        <Package className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Inventory Management:</strong>
          <ul className="mt-1 list-disc list-inside">
            <li><strong>Top input:</strong> Edit to <em>set/replace</em> the stock quantity (click "Save All" to apply)</li>
            <li><strong>+ Add field:</strong> Enter a number to <em>add</em> to existing stock (auto-saves on Enter or blur)</li>
          </ul>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-[250px]">
                  Product
                </th>
                <th className="text-center p-4 font-semibold text-gray-900 w-[100px]">SKU</th>
                {allSizes.map(size => (
                  <th key={size} className="text-center p-4 font-semibold text-gray-900 w-[120px]">
                    {size === 'OS' ? 'One Size' : size}
                  </th>
                ))}
                <th className="text-center p-4 font-semibold text-gray-900 w-[100px]">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.map(product => {
                const totalStock = product.sizes.reduce((acc, size) => 
                  acc + (product.inventory[size]?.quantity || 0), 0
                );
                
                return (
                  <tr key={product.id} className="hover:bg-gray-50">
                    {/* Product Info */}
                    <td className="p-4 sticky left-0 bg-white z-10">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {product.category}
                            </Badge>
                            {product.sizing_type === 'onesize' && (
                              <Badge className="text-xs bg-purple-100 text-purple-700">One Size</Badge>
                            )}
                            {product.sizing_type === 'ladyfit' && (
                              <Badge className="text-xs bg-pink-100 text-pink-700">Lady-fit</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* SKU */}
                    <td className="p-4 text-center">
                      <span className="font-mono text-sm text-gray-600">{product.sku}</span>
                    </td>

                    {/* Size Columns */}
                    {allSizes.map(size => {
                      const isValidSize = product.sizes.includes(size);
                      const inv = product.inventory[size] || { quantity: 0, eta: null };
                      const isOutOfStock = inv.quantity === 0;
                      const hasEta = inv.eta;
                      const isEditingEta = etaEditing === `${product.id}-${size}`;
                      
                      if (!isValidSize) {
                        return (
                          <td key={size} className="p-4 text-center bg-gray-50">
                            <span className="text-gray-300">—</span>
                          </td>
                        );
                      }

                      return (
                        <td key={size} className="p-4">
                          <div className="flex flex-col items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              value={inv.quantity}
                              onChange={(e) => handleQuantityChange(product.id, size, e.target.value, inv.eta)}
                              className={`w-20 text-center h-10 ${
                                isOutOfStock ? 'bg-red-50 border-red-200' : ''
                              } ${changes[`${product.id}-${size}`] ? 'ring-2 ring-yellow-300' : ''}`}
                            />
                            
                            {/* Add Stock Button */}
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-gray-400">+</span>
                              <Input
                                type="number"
                                min="0"
                                placeholder="+0"
                                value={addStockInputs[`${product.id}-${size}`] || ''}
                                onChange={(e) => handleAddStockChange(product.id, size, e.target.value, inv.eta, inv.quantity)}
                                onBlur={() => handleAddStockSubmit(product.id, size, inv.eta, inv.quantity)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleAddStockSubmit(product.id, size, inv.eta, inv.quantity);
                                  }
                                }}
                                className="w-14 text-center h-7 text-xs bg-green-50 border-green-200 placeholder:text-green-400"
                              />
                            </div>
                            
                            {/* ETA for out-of-stock */}
                            {isOutOfStock && (
                              <div className="mt-1">
                                {isEditingEta ? (
                                  <Input
                                    type="date"
                                    value={inv.eta || ''}
                                    onChange={(e) => handleEtaChange(product.id, size, e.target.value)}
                                    onBlur={() => setEtaEditing(null)}
                                    className="w-28 h-7 text-xs"
                                    autoFocus
                                  />
                                ) : hasEta ? (
                                  <button
                                    onClick={() => setEtaEditing(`${product.id}-${size}`)}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    <Calendar className="w-3 h-3" />
                                    {new Date(inv.eta).toLocaleDateString()}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setEtaEditing(`${product.id}-${size}`)}
                                    className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-700"
                                  >
                                    <AlertCircle className="w-3 h-3" />
                                    Set ETA
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}

                    {/* Total */}
                    <td className="p-4 text-center">
                      <span className={`font-semibold ${totalStock === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {totalStock}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-gray-900">{filteredProducts.length}</p>
            <p className="text-sm text-gray-600">Products</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {filteredProducts.reduce((acc, p) => 
                acc + p.sizes.reduce((a, s) => a + (p.inventory[s]?.quantity || 0), 0), 0
              ).toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">Total Units</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-600">
              {filteredProducts.filter(p => 
                p.sizes.every(s => (p.inventory[s]?.quantity || 0) === 0)
              ).length}
            </p>
            <p className="text-sm text-gray-600">Out of Stock</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-orange-600">
              {filteredProducts.filter(p => 
                p.sizes.some(s => (p.inventory[s]?.quantity || 0) === 0 && (p.inventory[s]?.quantity || 0) < 10)
              ).length}
            </p>
            <p className="text-sm text-gray-600">Low Stock</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInventory;
